<template>
  <div class="lv2-page">
    <div class="app-bg"></div>

    <header class="nav-shell">
      <nav class="nav" aria-label="Principal">
        <a href="#top" class="nav-brand" aria-label="NQA Inicio">
          <img :src="require('@/assets/images/LogoNQA.png')" alt="NQA" class="nav-logo" />
        </a>
        <ul>
          <li><a href="#producto">{{ labelsState['lv_nav_product'] || 'Product' }}</a></li>
          <li><a href="#integraciones">{{ labelsState['lv_nav_integrations'] || 'Integrations' }}</a></li>
          <li><a href="#casos">{{ labelsState['lv_nav_cases'] || 'Cases' }}</a></li>
          <li><a href="#precios">{{ labelsState['plans'] || 'Pricing' }}</a></li>
          <li><a href="#faq">FAQ</a></li>
        </ul>
        <div class="nav-cta">
          <div class="lang-switcher">
            <button class="btn btn-lang" @click="showLangDropdown = !showLangDropdown" :aria-label="'Idioma: ' + currentLanguage">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="16" height="16">
                <circle cx="12" cy="12" r="10"/>
                <path d="M2 12h20M12 2c2.5 3 4 6.5 4 10s-1.5 7-4 10c-2.5-3-4-6.5-4-10s1.5-7 4-10z"/>
              </svg>
              <span>{{ currentLanguage.toUpperCase() }}</span>
            </button>
            <div v-if="showLangDropdown" class="lang-dropdown">
              <button
                v-for="code in languageCodes"
                :key="code"
                @click="changeLanguage(code)"
                :class="{ 'lang-active': code === currentLanguage }"
              >
                {{ code.toUpperCase() }}
              </button>
            </div>
          </div>
          <a class="btn btn-ghost" :href="dashboardUrl" target="_blank">{{ labelsState['login'] || 'Log in' }}</a>
          <a class="btn btn-primary" :href="registerUrl" target="_blank" @click="trackDemoClick">
            {{ labelsState['lv_nav_demo'] || 'Free demo' }}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
              <path d="M5 12h14M13 5l7 7-7 7"/>
            </svg>
          </a>
        </div>
      </nav>
    </header>

    <main>
      <HeroV2 />
      <StatsV2 />
      <LogosV2 />
      <FeaturesV2 />
      <StepsV2 />
      <IntegrationsV2 />
      <CasesV2 />
      <PricingV2 />
      <TestimonialsV2 />
      <FAQV2 />
      <CTAV2 />
    </main>

    <footer class="lv2-footer">
      <div class="wrap">
        <div class="footer-grid">
          <div class="footer-brand">
            <div class="nav-brand">
              <img :src="require('@/assets/images/LogoNQA.png')" alt="NQA" class="nav-logo" style="height:40px" />
            </div>
            <p>{{ labelsState['lv_footer_desc'] || 'Conversational AI and automation platform for support, sales and operations teams in LATAM.' }}</p>
          </div>
          <div class="footer-col">
            <h4>{{ labelsState['lv_nav_product'] || 'Product' }}</h4>
            <ul>
              <li><a href="#producto">{{ labelsState['lv_footer_features'] || 'Features' }}</a></li>
              <li><a href="#integraciones">{{ labelsState['lv_nav_integrations'] || 'Integrations' }}</a></li>
              <li><a href="#precios">{{ labelsState['plans'] || 'Pricing' }}</a></li>
              <li><a href="#faq">FAQ</a></li>
            </ul>
          </div>
          <div class="footer-col">
            <h4>{{ labelsState['lv_footer_solutions'] || 'Solutions' }}</h4>
            <ul>
              <li><a href="#casos">{{ labelsState['lv_footer_health'] || 'Health' }}</a></li>
              <li><a href="#casos">{{ labelsState['lv_footer_retail'] || 'Retail' }}</a></li>
              <li><a href="#casos">{{ labelsState['lv_footer_services'] || 'Services' }}</a></li>
              <li><a href="#casos">{{ labelsState['lv_footer_education'] || 'Education' }}</a></li>
            </ul>
          </div>
          <div class="footer-col">
            <h4>{{ labelsState['about_nq_artificial'] || 'Company' }}</h4>
            <ul>
              <li><a href="/about">{{ labelsState['about_us'] || 'About NQA' }}</a></li>
              <li><a href="/blog">Blog</a></li>
              <li><a href="/contact-us">{{ labelsState['contact_us'] || 'Contact' }}</a></li>
              <li><a :href="dashboardUrl" target="_blank">Dashboard</a></li>
            </ul>
          </div>
          <div class="footer-col">
            <h4>Legal</h4>
            <ul>
              <li><a href="/TermsAndConditions">{{ labelsState['terms_and_conditions'] || 'Terms' }}</a></li>
              <li><a href="/Policies">{{ labelsState['policies'] || 'Privacy' }}</a></li>
              <li><a href="/DataTreatment">{{ labelsState['data_treatment'] || 'Habeas Data' }}</a></li>
            </ul>
          </div>
        </div>
        <div class="footer-bottom">
          <span>© 2026 NQ Artificial S.A.S. · {{ labelsState['lv_footer_made_in'] || 'Made in Colombia 🇨🇴' }}</span>
          <span>v2.0 · neuroquantum.engine</span>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { useLabelsStore } from '@/stores/labels';
import { useLanguageStore } from '@/stores/language';
import HeroV2         from '@/components/page_components/landing-v2/HeroV2.vue';
import StatsV2        from '@/components/page_components/landing-v2/StatsV2.vue';
import LogosV2        from '@/components/page_components/landing-v2/LogosV2.vue';
import FeaturesV2     from '@/components/page_components/landing-v2/FeaturesV2.vue';
import StepsV2        from '@/components/page_components/landing-v2/StepsV2.vue';
import IntegrationsV2 from '@/components/page_components/landing-v2/IntegrationsV2.vue';
import CasesV2        from '@/components/page_components/landing-v2/CasesV2.vue';
import PricingV2      from '@/components/page_components/landing-v2/PricingV2.vue';
import TestimonialsV2 from '@/components/page_components/landing-v2/TestimonialsV2.vue';
import FAQV2          from '@/components/page_components/landing-v2/FAQV2.vue';
import CTAV2          from '@/components/page_components/landing-v2/CTAV2.vue';
import { pixel } from '@/plugins/metaPixel'

export default {
  name: 'LandingV2',
  components: {
    HeroV2, StatsV2, LogosV2, FeaturesV2, StepsV2,
    IntegrationsV2, CasesV2, PricingV2, TestimonialsV2, FAQV2, CTAV2,
  },
  setup() {
    const labelsStore = useLabelsStore();
    const languageStore = useLanguageStore();

    const labelsState = computed(() => labelsStore.labels);
    const languageCodes = computed(() => languageStore.codes);
    const currentLanguage = computed(() => languageStore.currentLanguage);
    const showLangDropdown = ref(false);

    const changeLanguage = (code) => {
      languageStore.setLanguage(code);
      labelsStore.fetchAndSetLabels(code);
      showLangDropdown.value = false;
    };

    onMounted(() => {
      languageStore.initializeLanguage();
      labelsStore.fetchAndSetLabels(languageStore.language);
      pixel.viewContent({ name: 'Landing NQArtificial', category: 'Landing' })
    });

    const trackDemoClick = () => {
      pixel.lead({ name: 'Free Demo Button', category: 'LandingV2' })
    }

    return {
      labelsState,
      languageCodes,
      currentLanguage,
      showLangDropdown,
      changeLanguage,
      trackDemoClick,
      dashboardUrl: process.env.VUE_APP_DASHBOARD_URL || 'https://dashboard.nqartificial.com',
      registerUrl: `${process.env.VUE_APP_DASHBOARD_URL}/auth/cover-register`,
    };
  },
};
</script>

<style src="@/assets/css/landing-v2.css"></style>

<style scoped>
.lang-switcher {
  position: relative;
}

.btn-lang {
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;
  padding: 6px 12px !important;
  border-radius: 999px !important;
  border: 1px solid rgba(196, 204, 230, 0.25) !important;
  background: rgba(255, 255, 255, 0.06) !important;
  color: #c4cce6 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  transition: border-color 0.2s, background 0.2s !important;
  letter-spacing: 0.05em !important;
}

.btn-lang:hover {
  border-color: rgba(0, 204, 153, 0.5) !important;
  color: #00cc99 !important;
  background: rgba(0, 204, 153, 0.08) !important;
}

.lang-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #0d1f35;
  border: 1px solid rgba(196, 204, 230, 0.15);
  border-radius: 10px;
  padding: 6px;
  min-width: 80px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  z-index: 200;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.lang-dropdown button {
  display: block !important;
  width: 100% !important;
  padding: 8px 12px !important;
  border-radius: 6px !important;
  border: none !important;
  background: transparent !important;
  color: #c4cce6 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  text-align: left !important;
  letter-spacing: 0.05em !important;
  transition: background 0.15s, color 0.15s !important;
}

.lang-dropdown button:hover {
  background: rgba(0, 204, 153, 0.12) !important;
  color: #00cc99 !important;
}

.lang-dropdown button.lang-active {
  color: #00cc99 !important;
  background: rgba(0, 204, 153, 0.1) !important;
}
</style>
